import {Injectable} from "@angular/core";
import {VisualDoorRenderer} from "./visual-door-renderer";
import {CanvasCache, CanvasType} from "../caches/canvas-cache";
import {ShadowInpaintingRenderer} from "./shadow-inpainting-renderer";
import {
  ClippedCorners,
  Corners,
  DoorCorners,
  LightPosition,
  Point,
  ProductsMap,
  RenderOptions,
  RenderResult,
  Size
} from "../types";
import {loadImage} from "../helper";
import {ImageCache} from "../caches/image-cache";

export interface RenderedScenario {
  image: HTMLImageElement
  positionInCanvas: Point
  corners: Array<DoorCorners>
  size: Size
  lightPosition: LightPosition
  shadowImageUrl?: string
}

@Injectable({
  providedIn: 'root'
})
export class ScenarioRenderer {
  constructor(
    private readonly canvasCache: CanvasCache,
    private readonly imageCache: ImageCache,
    private readonly visualDoorRenderer: VisualDoorRenderer,
    private readonly shadowInpaintingRenderer: ShadowInpaintingRenderer
  ) {
  }

  public async renderScenario(
    {canvas, scenario, products}: {
      canvas: HTMLCanvasElement,
      scenario: RenderedScenario,
      products: ProductsMap,
    }, options: RenderOptions): Promise<RenderResult> {

    if (canvas.width === 0 || canvas.height === 0) {
      console.warn('Canvas has no height or width')
      return {}
    }

    // Create a canvas which represents the current scenario
    console.time('- create canvas')
    const {context: temporaryContext, canvas: temporaryCanvas} = this.canvasCache.get(CanvasType.SCENARIO, {
      width: scenario.image.width,
      height: scenario.image.height
    })
    console.timeEnd('- create canvas')

    // Draw the scenario image on the canvas
    temporaryContext.drawImage(scenario.image, 0, 0);

    let doorBrightness = undefined

    if (options.showDoor) {
      const cornersWithSelectedDoor = scenario.corners.filter(c => !!products.get(c.index)?.door)

      // Draw doors
      for (const corners of cornersWithSelectedDoor) {
        await this.visualDoorRenderer.renderVisualDoor({
          scenario,
          renderingContext: temporaryContext,
          corners,
          products: products.get(corners.index),
        }, options)
      }

      // Calculate door brightness
      if (options.calculateDoorBrightness) {
        const firstDoorCorners = cornersWithSelectedDoor.length > 0 ? cornersWithSelectedDoor[0] : null
        if (firstDoorCorners) {
          const firstDoor = products.get(firstDoorCorners.index)?.door
          if (firstDoor) {
            const image = await this.imageCache.get({url: firstDoor.visualisationImageUrl!})
            console.time(' - Get door image brightness')
            doorBrightness = await this.getDoorImageBrightness(image)
            console.timeEnd(' - Get door image brightness')
            console.log('door brightness', firstDoorCorners)
          }
        }
      }

      // Draw scenario shadow image
      if (options.enableShadowInpainting) {
        console.time('- Draw scenario shadow image')
        await this.shadowInpaintingRenderer.drawScenarioShadowImage({
          renderingContext: temporaryContext,
          doorCorners: cornersWithSelectedDoor[0],
          scenario,
          renderOptions: options
        })
        console.timeEnd('- Draw scenario shadow image')
      }
    }

    // Draw the final image on the outer canvas
    console.time('- Draw scenario image')
    const context = canvas.getContext('2d')!;


    context.clearRect(0, 0, canvas.width, canvas.height);

    if (temporaryCanvas.width === 0 || temporaryCanvas.height === 0) {
      console.warn('temporaryCanvas has no height or width', {
        width: temporaryCanvas.width,
        height: temporaryCanvas.height
      })
      return {}
    }

    context.drawImage(temporaryCanvas, scenario.positionInCanvas.x, scenario.positionInCanvas.y, scenario.size.width, scenario.size.height);
    console.timeEnd('- Draw scenario image')

    return { doorBrightness }
  }

  /**
   * Clips the rendering context to the corners of the door
   * Followed drawing will only be visible within the clipped area
   */
  public static clipCorners({renderingContext, corners}: {
    renderingContext: CanvasRenderingContext2D,
    corners: Corners & ClippedCorners
  }) {
    this.drawCornersPath({renderingContext, corners})
    renderingContext.clip();
  }

  /**
   * Draws a path around the corners of the door
   */
  public static drawCornersPath({renderingContext, corners}: {
    renderingContext: CanvasRenderingContext2D,
    corners: Corners & ClippedCorners
  }) {
    renderingContext.beginPath();
    if (corners.hasClippedCorners) {
      renderingContext.moveTo(corners.topLeftBottom!.x, corners.topLeftBottom!.y);
      renderingContext.lineTo(corners.topLeftRight!.x, corners.topLeftRight!.y);
      renderingContext.lineTo(corners.topRightLeft!.x, corners.topRightLeft!.y);
      renderingContext.lineTo(corners.topRightBottom!.x, corners.topRightBottom!.y);
      renderingContext.lineTo(corners.bottomRight.x, corners.bottomRight.y);
      renderingContext.lineTo(corners.bottomLeft.x, corners.bottomLeft.y);
      renderingContext.lineTo(corners.topLeftBottom!.x, corners.topLeftBottom!.y);
    } else {
      // Add each door's path to the combined path
      renderingContext.moveTo(corners.topLeft.x, corners.topLeft.y);
      renderingContext.lineTo(corners.topRight.x, corners.topRight.y);
      renderingContext.lineTo(corners.bottomRight.x, corners.bottomRight.y);
      renderingContext.lineTo(corners.bottomLeft.x, corners.bottomLeft.y);
      renderingContext.lineTo(corners.topLeft.x, corners.topLeft.y);
    }
    renderingContext.closePath();
  }

  private async getDoorImageBrightness(image: HTMLImageElement): Promise<number> {
    const { canvas, context } = this.canvasCache.get(CanvasType.DOOR, {
      width: image.width,
      height: image.height
    })

    // Draw downscaled image on canvas since we don't need the full resolution to calculate brightness
    const maxLength = 100;
    const scaleFactor = Math.max(image.width, image.height) / maxLength;
    canvas.width = Math.round(image.width / scaleFactor);
    canvas.height = Math.round(image.height / scaleFactor);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    const data = context.getImageData(0, 0, canvas.width, canvas.height).data
    let totalBrightness = 0;
    let pixelCount = 0;

    for (let i = 0; i < data.length; i += 4) {
      const alpha = data[i + 3];
      if (alpha > 0) {
        const brightness = (data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114); // Convert to grayscale
        totalBrightness += brightness;
        pixelCount++;
      }
    }

    // We want to get an output of 0-100
    return Math.round((totalBrightness / pixelCount / 255) * 100)
  }
}
